import ArrowLeft from '@assets/icons/arrow-left.svg';
import CaseyShortIcon from '@assets/icons/casey-short.svg';
import EmptyMenuIcon from '@assets/icons/no-employees-icon.svg';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, LIST_MARGIN_HORIZONTAL, TYPOGRAPHY } from '@/styles';
import { ModalContainer } from '@/components/modal-container';
import { ScrollView } from 'react-native-gesture-handler';
import { Text, View } from 'react-native';
import { YelpMenuItem } from './types';

const CREATE_ORDER_GUIDE_BUTTON_HEIGHT = 60;
const CREATE_ORDER_GUIDE_BUTTON_PADDING = 40;

interface Props {
  generateOrderGuideLoading: boolean;
  menuItems?: YelpMenuItem[];
  menuItemsLoading?: boolean;
  menuItemsLoadingMessage?: string;
  noMenuItemsMessage: string;
  noMenuItemsTitle: string;
  onGenerateOrderGuide: () => void;
  restaurantName: string;
  restaurantUUID: string;
  showGenerateOrderGuideButton: boolean;
}

export const ViewMenuModal: React.FC<Props> = ({
  generateOrderGuideLoading,
  menuItems,
  menuItemsLoading,
  menuItemsLoadingMessage,
  noMenuItemsMessage,
  noMenuItemsTitle,
  onGenerateOrderGuide,
  restaurantName,
  restaurantUUID,
  showGenerateOrderGuideButton,
}) => {
  const [styles, theme] = useStyles();

  const categoryToMenuItems = useMemo(() => {
    return menuItems?.reduce(
      (mapping, menuItem) => {
        mapping[menuItem.category] = mapping[menuItem.category] || [];
        mapping[menuItem.category].push(menuItem);

        return mapping;
      },
      {} as {
        [category: string]: YelpMenuItem[];
      },
    );
  }, [menuItems]);

  return (
    <ModalContainer
      navigationHeaderProps={{
        BackButtonIcon: ArrowLeft,
        showBackButton: true,
        showShadow: true,
        title: `${restaurantName} Menu`,
      }}
      showLargeLayoutCloseButton={false}
    >
      {menuItemsLoading ? (
        <View style={styles.loadingWrapper}>
          <ActivityIndicator color={theme.colors.primary} size="large" />
          <Text style={[TYPOGRAPHY.medium, styles.loadingText]}>
            {menuItemsLoadingMessage ?? ''}
          </Text>
        </View>
      ) : categoryToMenuItems && Object.keys(categoryToMenuItems).length ? (
        <>
          <ScrollView alwaysBounceVertical={false}>
            {Object.keys(categoryToMenuItems).map((category) => {
              return (
                <View key={category} style={styles.menuCategoryWrapper}>
                  <Text style={[FONTS.primaryBold, styles.menuCategory]}>
                    {category}
                  </Text>
                  {categoryToMenuItems[category].map((item, index) => (
                    <View key={index} style={styles.menuItemList}>
                      <Text style={[FONTS.primaryBold, styles.menuItemName]}>
                        {item.item_name}
                      </Text>
                      <Text
                        style={[
                          FONTS.primary,
                          styles.menuItemIngredients,
                          !item.ingredients.length &&
                            styles.menuItemIngredientsMissing,
                        ]}
                      >
                        {item.ingredients.length
                          ? item.ingredients.join(', ')
                          : '--'}
                      </Text>
                    </View>
                  ))}
                </View>
              );
            })}
            {/*
            Add a spacer to the bottom of the ScrollView so that the floating Create Order Guide
            button doesn't overlap with the text when the view is scrolled down.
            */}
            {showGenerateOrderGuideButton ? (
              <View
                style={{
                  height:
                    CREATE_ORDER_GUIDE_BUTTON_HEIGHT +
                    CREATE_ORDER_GUIDE_BUTTON_PADDING,
                }}
              />
            ) : null}
          </ScrollView>
          {showGenerateOrderGuideButton ? (
            <Button
              containerStyle={styles.generateOrderGuideWrapper}
              leftElement={<CaseyShortIcon height={32} width={32} />}
              loading={generateOrderGuideLoading}
              onPress={onGenerateOrderGuide}
              rightElement={<View style={styles.iconContainer} />}
              trackingData={{
                name: 'view_account.create_order_guide',
                properties: {
                  restaurantUUID,
                },
              }}
            >
              {i18n.t('generate_order_guide')}
            </Button>
          ) : null}
        </>
      ) : (
        <View style={styles.missingMenuWrapper}>
          <EmptyMenuIcon fill={theme.colors.accent} />
          <Text style={[TYPOGRAPHY.h2, styles.missingMenuTitle]}>
            {noMenuItemsTitle}
          </Text>
          <Text style={[TYPOGRAPHY.medium, styles.missingMenuText]}>
            {noMenuItemsMessage}
          </Text>
        </View>
      )}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  generateOrderGuideWrapper: {
    alignItems: 'center',
    bottom: CREATE_ORDER_GUIDE_BUTTON_PADDING,
    left: 0,
    position: 'absolute',
    right: 0,
  },
  iconContainer: {
    height: 32,
    width: 32,
  },
  loadingText: {
    color: theme.colors.primary,
    paddingTop: 16,
    textAlign: 'center',
  },
  loadingWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  menuCategory: {
    color: theme.colors.inactiveText,
    fontSize: 16,
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
    textTransform: 'uppercase',
  },
  menuCategoryWrapper: {
    paddingTop: 30,
  },
  menuItemIngredients: {
    color: theme.colors.primary,
    fontSize: 14,
    paddingTop: 4,
    textTransform: 'capitalize',
  },
  menuItemIngredientsMissing: {
    color: theme.colors.inactiveText,
  },
  menuItemList: {
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
    paddingTop: 16,
  },
  menuItemName: {
    color: theme.colors.primary,
    fontSize: 16,
    textTransform: 'capitalize',
  },
  missingMenuText: {
    color: theme.colors.primary,
    paddingTop: 8,
    textAlign: 'center',
  },
  missingMenuTitle: {
    color: theme.colors.primary,
    paddingTop: 16,
    textAlign: 'center',
  },
  missingMenuWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 56,
    paddingTop: 60,
  },
}));
